import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Plus, Trash2, Pencil, X, Search, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import AuthedImage from "@/components/AuthedImage";

const CATEGORIES = [
  "Timber Panels", "Wall Battens", "Feature Panels", "Cabinetry", "Shelving",
  "LED Lighting", "Electric Fireplaces", "TV Brackets", "Hardware", "Labour",
  "Finishing Materials", "Installation Services",
  "Stone", "Timber", "Marble", "Travertine", "Microcement", "Plaster", "Fluted Panels",
  "Materials", "Other",
];
const UNITS = ["each", "metre", "square metre", "sheet", "panel", "linear metre", "hour", "fixed"];
const AVAILABILITIES = ["available", "limited", "special_order", "temporarily_unavailable", "discontinued"];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = {};
    if (cat !== "all") params.category = cat;
    if (q) params.q = q;
    const { data } = await api.get("/products", { params });
    setProducts(data);
  };
  useEffect(() => { load(); }, [cat, q]); // eslint-disable-line

  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/products/${editing.id}`, data);
      else await api.post("/products", data);
      toast.success(editing?.id ? "Product updated" : "Product added");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-7xl" data-testid="products-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline">Studio · Library</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Materials Library</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Your palette of timbers, stones, laminates and finishes. Add a material once, then use it across every project you design.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/materials/import" data-testid="btn-import-materials" className="btn-outline">
            <Upload className="w-4 h-4" strokeWidth={1.5}/> Import
          </Link>
          <Link to="/materials/thumbnails" data-testid="btn-thumbnails" className="btn-outline">
            <Wand2 className="w-4 h-4" strokeWidth={1.5}/> Thumbnails
          </Link>
          <button data-testid="btn-add-product" onClick={() => { setEditing({}); setOpen(true); }} className="btn-primary">
            <Plus className="w-4 h-4" strokeWidth={1.5}/> Add custom material
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-8">
        <div className="flex-1 min-w-[240px]">
          <input data-testid="input-search-product"
                 aria-label="Filter materials"
                 placeholder="Filter materials…"
                 className="luxe-input"
                 value={q}
                 onChange={(e) => setQ(e.target.value)}/>
        </div>
        <select data-testid="select-category-filter" value={cat} onChange={(e) => setCat(e.target.value)} className="luxe-input max-w-xs">
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {products.length === 0 ? (
        <div className="luxe-card p-16 text-center" data-testid="materials-empty-state">
          <div className="font-serif text-3xl mb-3">Build your material library</div>
          <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
            Add the finishes and materials you love working with — timber, stone, laminate, microcement, cabinetry. You'll pull them straight into every project.
          </p>
          <button data-testid="btn-add-first-product" onClick={() => { setEditing({}); setOpen(true); }} className="btn-accent mx-auto">
            <Plus className="w-4 h-4" strokeWidth={1.5}/> Add custom material
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {products.map((p) => {
            // Simplified surface: keep the visual card focused on
            // "what does this material look like and what is it?".
            // Cost/margin/supplier metadata stays available inside
            // the edit modal (Details + Pricing tabs).
            const finish = [p.colour, p.finish].filter(Boolean).join(" · ");
            const isStarter = p.scope === "global" && p.origin === "starter";
            return (
              <div key={p.id} className="bg-card group cursor-pointer relative" data-testid={`product-card-${p.id}`}
                   onClick={() => { setEditing(p); setOpen(true); }}>
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  <AuthedImage path={p.image_path} alt={p.name} className="w-full h-full object-cover"/>
                </div>
                {isStarter && (
                  <div className="absolute top-3 left-3 bg-background/85 backdrop-blur-sm border border-border px-2 py-0.5 text-[9px] uppercase tracking-widest text-muted-foreground"
                       data-testid={`starter-badge-${p.id}`}
                       title="Subtend Studio starter material — read-only. Assign it to a design and Subtend Studio will add an editable copy to your library.">
                    Starter
                  </div>
                )}
                <div className="p-6">
                  <div className="overline text-[10px]">{p.category}</div>
                  <div className="font-serif text-xl mt-2 leading-tight">{p.name}</div>
                  {finish && (
                    <div className="text-xs text-muted-foreground mt-1 truncate" data-testid={`product-finish-${p.id}`}>{finish}</div>
                  )}
                  <div className="flex gap-2 mt-5 pt-4 border-t border-border">
                    {isStarter ? (
                      <button data-testid={`btn-add-to-library-${p.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const { data } = await api.post(`/products/${p.id}/copy-starter`);
                                  toast.success("Added to your library");
                                  await load();
                                  setEditing(data); setOpen(true);
                                } catch (err) { toast.error(formatErr(err)); }
                              }}
                              className="btn-outline flex-1 justify-center text-xs">
                        Add to my library
                      </button>
                    ) : (
                      <>
                        <button data-testid={`btn-edit-product-${p.id}`}
                                onClick={(e) => { e.stopPropagation(); setEditing(p); setOpen(true); }}
                                className="btn-outline flex-1 justify-center text-xs">
                          <Pencil className="w-3 h-3" strokeWidth={1.5}/> Edit
                        </button>
                        <button data-testid={`btn-delete-product-${p.id}`}
                                onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                                className="btn-outline text-xs text-[color:hsl(var(--destructive))]">
                          <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && <ProductForm product={editing} onClose={() => { setOpen(false); setEditing(null); }} onSave={save} />}
    </div>
  );
}

function ProductForm({ product, onClose, onSave }) {
  const isStarter = product && product.scope === "global" && product.origin === "starter";
  const [form, setForm] = useState({
    name: "", description: "", category: "Materials", unit: "each",
    cost_price: 0, unit_price: 0, image_path: null,
    supplier_id: null, brand: "", range: "", sku: "", variant: "",
    colour: "", finish: "", lead_time: "", availability: "available",
    wastage_pct: 0, minimum_order: 0, customer_visible: false,
    ...product,
  });
  const [suppliers, setSuppliers] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [assets, setAssets] = useState([]);
  // Stage C: Material Admin Visual Assets tab (versioned PBR-ready assets).
  const [tab, setTab] = useState("details");   // details | pricing | visual
  const [visualAssets, setVisualAssets] = useState([]);
  const fileRef = useRef();
  const loadVisualAssets = async () => {
    if (!product?.id) return;
    try {
      const r = await api.get(`/materials/${product.id}/visual-assets?include_superseded=true`);
      setVisualAssets(r.data || []);
    } catch (_) { /* material may not have any yet */ }
  };
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    if (product?.id) {
      api.get(`/products/${product.id}/prices`).then((r) => setPriceHistory(r.data)).catch(() => {});
      api.get(`/products/${product.id}/assets`).then((r) => setAssets(r.data)).catch(() => {});
      loadVisualAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  const setNum = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 });
  const margin = form.unit_price > 0 ? (((form.unit_price - form.cost_price) / form.unit_price) * 100).toFixed(1) : "0.0";

  const catOptions = form.category && !CATEGORIES.includes(form.category) ? [form.category, ...CATEGORIES] : CATEGORIES;
  const unitOptions = form.unit && !UNITS.includes(form.unit) ? [form.unit, ...UNITS] : UNITS;

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "products");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.storage_path }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="product-form-modal">
      <div className="bg-card max-w-3xl w-full p-8 border border-border my-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline">{isStarter ? "Subtend Starter" : (product?.id ? "Edit" : "New")}</div>
            <h3 className="font-serif font-light text-3xl mt-2">{product?.name || "Add material"}</h3>
          </div>
          <button data-testid="btn-close-product-form" onClick={onClose}><X strokeWidth={1.5}/></button>
        </div>

        {isStarter && (
          <div className="luxe-card p-5 mb-6" data-testid="starter-readonly-notice">
            <div className="overline text-[10px] mb-2" style={{ color: "#A68A64" }}>Read-only</div>
            <p className="text-sm text-muted-foreground">
              This is a Subtend Studio starter material — a curated finish shared across all studios. It cannot be edited directly.
              Add it to your library to create your own editable copy with pricing, supplier and finish overrides.
            </p>
            <div className="mt-4 flex flex-wrap items-start gap-3">
              <button data-testid="btn-add-to-library-modal"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/products/${product.id}/copy-starter`);
                          toast.success("Added to your library");
                          onSave && onSave({ __refresh: true });
                          onClose();
                          setTimeout(() => {
                            const evt = new CustomEvent("luxe:open-material", { detail: data });
                            window.dispatchEvent(evt);
                          }, 50);
                        } catch (err) { toast.error(formatErr(err)); }
                      }}
                      className="btn-primary text-sm">
                Add to my library
              </button>
              <div className="text-xs text-muted-foreground">
                Category · <span className="font-medium">{product.category}</span>
                {product.colour ? <> · Colour · <span className="font-medium">{product.colour}</span></> : null}
                {product.finish ? <> · Finish · <span className="font-medium">{product.finish}</span></> : null}
              </div>
            </div>
          </div>
        )}

        {isStarter ? (
          <div className="grid md:grid-cols-[240px_1fr] gap-6">
            <div className="aspect-square border border-dashed border-input bg-muted/40 overflow-hidden">
              {form.image_path
                ? <AuthedImage path={form.image_path} alt={form.name} className="w-full h-full object-cover"/>
                : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>}
            </div>
            <div className="space-y-2 text-sm">
              {form.description && <p>{form.description}</p>}
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mt-4">
                {form.thickness_mm ? <><dt className="text-muted-foreground">Thickness</dt><dd>{form.thickness_mm} mm</dd></> : null}
                {form.slab_width_mm ? <><dt className="text-muted-foreground">Slab width</dt><dd>{form.slab_width_mm} mm</dd></> : null}
                {form.slab_height_mm ? <><dt className="text-muted-foreground">Slab height</dt><dd>{form.slab_height_mm} mm</dd></> : null}
                {form.texture ? <><dt className="text-muted-foreground">Texture</dt><dd>{form.texture}</dd></> : null}
                {form.subcategory ? <><dt className="text-muted-foreground">Subcategory</dt><dd>{form.subcategory}</dd></> : null}
              </dl>
            </div>
          </div>
        ) : (
        <>
        {/* Stage C: three-tab split so financial Pricing history and
            immutable versioned Visual Assets never cross-pollute. */}
        {product?.id && (
          <div className="flex gap-1 mb-4 border-b border-border"
               role="tablist" data-testid="product-form-tabs">
            {[
              { v: "details", l: "Details" },
              { v: "pricing", l: "Pricing" },
              { v: "visual",  l: "Visual Assets" },
            ].map((t) => (
              <button key={t.v} role="tab"
                      data-testid={`product-form-tab-${t.v}`}
                      aria-selected={tab === t.v}
                      onClick={() => setTab(t.v)}
                      className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t.v ? "border-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {t.l}
              </button>
            ))}
          </div>
        )}

        {tab !== "visual" ? (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Image</label>
            <div className="aspect-square border border-dashed border-input bg-muted/40 flex items-center justify-center overflow-hidden">
              {form.image_path ? (
                <AuthedImage path={form.image_path} alt="" className="w-full h-full object-cover"/>
              ) : (
                <div className="text-muted-foreground text-xs">No image</div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
            <div className="flex gap-2 mt-3">
              <button data-testid="btn-upload-product-image" type="button" onClick={() => fileRef.current.click()} className="btn-outline flex-1 justify-center text-xs">
                <Upload className="w-3 h-3" strokeWidth={1.5}/> {form.image_path ? "Replace" : "Upload"}
              </button>
              {form.image_path && (
                <button data-testid="btn-remove-product-image" type="button"
                        onClick={() => setForm((f) => ({ ...f, image_path: null }))}
                        className="btn-outline text-xs px-3" title="Remove image">
                  <X className="w-3 h-3" strokeWidth={1.5}/>
                </button>
              )}
            </div>

            {priceHistory.length > 0 && (
              <div className="mt-6" data-testid="price-history">
                <div className="overline text-[10px] mb-2">Price history</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {priceHistory.map((p) => (
                    <div key={p.id} className="text-xs flex justify-between border-b border-border py-1">
                      <span className="text-muted-foreground">{new Date(p.created_at).toLocaleDateString("en-AU")}</span>
                      <span>${(p.cost_price_cents/100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {assets.length > 0 && (
              <div className="mt-6" data-testid="digital-assets">
                <div className="overline text-[10px] mb-2">Digital assets</div>
                <ul className="space-y-1 max-h-40 overflow-y-auto text-xs">
                  {assets.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 border-b border-border py-1"
                        data-testid={`asset-${a.id}`}>
                      <span className="capitalize truncate">
                        {String(a.asset_type || "").replace(/_/g, " ")}
                        {a.is_render_asset && <span className="ml-2 text-[9px] uppercase tracking-widest text-[color:hsl(var(--accent))]">render</span>}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {a.storage_path !== form.image_path && a.asset_type !== "thumbnail" && (
                          <button type="button"
                                  data-testid={`btn-promote-asset-${a.id}`}
                                  onClick={async () => {
                                    try {
                                      const r = await api.post(`/products/${product.id}/assets/${a.id}/promote`);
                                      setForm((f) => ({ ...f, image_path: r.data.image_path }));
                                      toast.success("Set as primary");
                                    } catch (e) { toast.error(formatErr(e)); }
                                  }}
                                  className="text-[10px] px-1.5 py-0.5 border border-input hover:border-foreground">
                            Use
                          </button>
                        )}
                        <span className="text-muted-foreground text-[10px]">{a.width || 0}×{a.height || 0}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="text-[10px] text-muted-foreground mt-2">
                  Supplier full-sheet and high-resolution swatches are retained for future rendering.
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-2 space-y-4">
            <Field label="Name *" testid="input-product-name" value={form.name} onChange={set("name")}/>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Category</label>
                <select data-testid="select-product-category" className="luxe-input" value={form.category} onChange={set("category")}>
                  {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Unit</label>
                <select data-testid="select-product-unit" className="luxe-input" value={form.unit} onChange={set("unit")}>
                  {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Colour" testid="input-product-colour" value={form.colour || ""} onChange={set("colour")}/>
              <Field label="Finish" testid="input-product-finish" value={form.finish || ""} onChange={set("finish")}/>
            </div>

            <div>
              <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Description</label>
              <textarea data-testid="input-product-desc" rows="3" className="luxe-input" value={form.description || ""} onChange={set("description")}/>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Cost price (AUD)</label>
                <input data-testid="input-product-cost" type="number" step="0.01" className="luxe-input" value={form.cost_price} onChange={setNum("cost_price")}/>
              </div>
              <div>
                <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Sell price (AUD)</label>
                <input data-testid="input-product-price" type="number" step="0.01" className="luxe-input" value={form.unit_price} onChange={setNum("unit_price")}/>
              </div>
              <div>
                <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Margin</label>
                <div className="luxe-input bg-muted/40" data-testid="product-margin">{margin}%</div>
              </div>
            </div>

            {/* Advanced fields — hidden by default to keep the form
                friendly for owner-operators. Everything under the
                <details> is unchanged behaviour, just re-organised. */}
            <details className="border border-border" data-testid="product-advanced-details">
              <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground select-none hover:text-foreground"
                       data-testid="product-advanced-summary">
                Advanced fields
              </summary>
              <div className="p-4 space-y-4 border-t border-border">
                <div>
                  <label className="overline block mb-2">Supplier</label>
                  <select data-testid="select-product-supplier" className="luxe-input" value={form.supplier_id || ""} onChange={set("supplier_id")}>
                    <option value="">— none —</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Field label="Brand" testid="input-product-brand" value={form.brand || ""} onChange={set("brand")}/>
                  <Field label="Range" testid="input-product-range" value={form.range || ""} onChange={set("range")}/>
                  <Field label="SKU" testid="input-product-sku" value={form.sku || ""} onChange={set("sku")}/>
                  <Field label="Variant" testid="input-product-variant" value={form.variant || ""} onChange={set("variant")}/>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="overline block mb-2">Availability</label>
                    <select data-testid="select-product-availability" className="luxe-input" value={form.availability} onChange={set("availability")}>
                      {AVAILABILITIES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <Field label="Lead time" testid="input-product-leadtime" value={form.lead_time || ""} onChange={set("lead_time")}/>
                  <div>
                    <label className="overline block mb-2">Wastage %</label>
                    <input type="number" step="0.5" data-testid="input-product-wastage" className="luxe-input" value={form.wastage_pct || 0} onChange={setNum("wastage_pct")}/>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="chk-customer-visible" checked={!!form.customer_visible} onChange={setBool("customer_visible")}/>
                  Show on public /materials page
                </label>
              </div>
            </details>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button data-testid="btn-save-product" onClick={() => onSave(form)} className="btn-primary">Save material</button>
        </div>
        </>
        ) : (
          <VisualAssetsTab
            materialId={product?.id}
            assets={visualAssets}
            onReload={loadVisualAssets}
          />
        )}
        </>
        )}
      </div>
    </div>
  );
}

// ── Visual Assets Tab (Stage C) ────────────────────────────────────
// Wired to /materials/{mid}/visual-assets endpoints. Version history,
// lifecycle, readiness, supersede, delete. All readiness values come
// from the backend classifier — no frontend invention.
const READINESS_LABEL = {
  full_pbr_ready:     "Full PBR Ready",
  threed_texture_ready: "3D Texture Ready",
  ai_reference_ready: "AI Reference Ready",
  thumbnail_only:     "Thumbnail Only",
};
const ASSET_KEYS = ["thumbnail", "albedo", "normal", "roughness", "displacement", "opacity"];

function VisualAssetsTab({ materialId, assets, onReload }) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    asset_paths: {},
    physical_texture_width_mm:  "",
    physical_texture_height_mm: "",
  });
  const upload = async (key, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "visual_assets");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, asset_paths: { ...(d.asset_paths || {}), [key]: data.storage_path } }));
      toast.success(`${key} uploaded`);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const save = async () => {
    if (!materialId) return;
    const body = {
      material_id: materialId,
      asset_paths: draft.asset_paths || {},
      ...(draft.physical_texture_width_mm ? { physical_texture_width_mm:  parseFloat(draft.physical_texture_width_mm) } : {}),
      ...(draft.physical_texture_height_mm ? { physical_texture_height_mm: parseFloat(draft.physical_texture_height_mm) } : {}),
    };
    try {
      const active = assets.find((a) => a.status === "active");
      if (active) {
        await api.post(`/materials/${materialId}/visual-assets/${active.id}/supersede`, body);
        toast.success("New version superseding previous active");
      } else {
        await api.post(`/materials/${materialId}/visual-assets`, body);
        toast.success("Visual asset created");
      }
      setCreating(false);
      setDraft({ asset_paths: {}, physical_texture_width_mm: "", physical_texture_height_mm: "" });
      onReload();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this visual asset version? This is only possible if no Design Option pinned it.")) return;
    try {
      await api.delete(`/materials/${materialId}/visual-assets/${id}`);
      toast.success("Visual asset deleted");
      onReload();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid="visual-assets-tab" className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-[10px]">Immutable version history</div>
            <div className="text-xs text-muted-foreground mt-1">
              Superseding creates a new active version. Old versions remain resolvable for pinned Design Options.
              Readiness classification is backend-derived from the uploaded maps.
            </div>
          </div>
          {!creating && (
            <button data-testid="btn-new-visual-asset"
                    onClick={() => setCreating(true)} className="btn-primary text-sm">
              <Plus className="w-3 h-3" strokeWidth={1.5}/>
              {assets.some((a) => a.status === "active") ? "Supersede active version" : "Create first version"}
            </button>
          )}
        </div>
        {assets.length === 0 && !creating && (
          <div className="p-6 border border-dashed border-border text-center text-sm text-muted-foreground"
               data-testid="visual-assets-empty">
            No visual asset versions yet. Materials without an active asset resolve to
            "Render Asset Missing" in Design Option Readiness.
          </div>
        )}
        {assets.length > 0 && (
          <ul className="border border-border divide-y divide-border">
            {assets.map((a) => (
              <li key={a.id}
                  data-testid={`visual-asset-row-${a.id}`}
                  className={`p-3 flex items-center gap-3 ${a.status === "active" ? "bg-muted/30" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm"
                          data-testid={`visual-asset-version-${a.id}`}>V{a.version}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${a.status === "active" ? "border-foreground" : "border-border text-muted-foreground"}`}
                          data-testid={`visual-asset-status-${a.id}`}>
                      {a.status}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground"
                          data-testid={`visual-asset-readiness-${a.id}`}>
                      {READINESS_LABEL[a.readiness_level] || a.readiness_level || "unclassified"}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                    {ASSET_KEYS.map((k) => a.asset_paths?.[k] && <span key={k}>{k}</span>)}
                  </div>
                  {(a.physical_texture_width_mm || a.physical_texture_height_mm) && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {a.physical_texture_width_mm || "?"} × {a.physical_texture_height_mm || "?"} mm
                    </div>
                  )}
                </div>
                {a.status !== "active" || assets.filter((x) => x.status === "active").length === 0 ? (
                  <button data-testid={`btn-delete-visual-asset-${a.id}`}
                          onClick={() => remove(a.id)}
                          className="text-xs text-destructive hover:underline"
                          title="Delete this version (only allowed if no Option pinned it)">
                    Delete
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating && (
        <div className="border border-border p-4 space-y-3" data-testid="visual-asset-form">
          <div className="overline text-[10px]">New visual asset version</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {ASSET_KEYS.map((key) => (
              <VisualAssetSlot key={key} label={key} value={draft.asset_paths?.[key]}
                onUpload={(f) => upload(key, f)}
                onClear={() => setDraft((d) => {
                  const ap = { ...(d.asset_paths || {}) }; delete ap[key];
                  return { ...d, asset_paths: ap };
                })}/>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-[10px] block mb-1">Physical texture width (mm)</label>
              <input data-testid="visual-asset-width"
                     type="number" className="luxe-input"
                     value={draft.physical_texture_width_mm}
                     onChange={(e) => setDraft((d) => ({ ...d, physical_texture_width_mm: e.target.value }))}/>
            </div>
            <div>
              <label className="overline text-[10px] block mb-1">Physical texture height (mm)</label>
              <input data-testid="visual-asset-height"
                     type="number" className="luxe-input"
                     value={draft.physical_texture_height_mm}
                     onChange={(e) => setDraft((d) => ({ ...d, physical_texture_height_mm: e.target.value }))}/>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn-outline text-sm"
                    onClick={() => { setCreating(false); setDraft({ asset_paths: {}, physical_texture_width_mm: "", physical_texture_height_mm: "" }); }}>
              Cancel
            </button>
            <button data-testid="btn-save-visual-asset"
                    className="btn-primary text-sm" onClick={save}>
              Save version
            </button>
          </div>
          <div className="text-[10px] text-muted-foreground">
            Readiness will be auto-classified by the backend from the uploaded maps:
            albedo+normal+roughness ⇒ Full PBR Ready · albedo+one ⇒ 3D Texture Ready · albedo ⇒ AI Reference Ready · else Thumbnail Only.
          </div>
        </div>
      )}
    </div>
  );
}

function VisualAssetSlot({ label, value, onUpload, onClear }) {
  const ref = useRef();
  return (
    <div className="min-w-0">
      <label className="overline text-[10px] block mb-1 capitalize">{label}</label>
      <div className="aspect-square border border-dashed border-input bg-muted/40 flex items-center justify-center overflow-hidden text-[10px] text-muted-foreground">
        {value ? <AuthedImage path={value} alt={label} className="w-full h-full object-cover"/> : "—"}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden"
             onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}/>
      <div className="flex gap-1 mt-1">
        <button type="button"
                data-testid={`va-upload-${label}`}
                onClick={() => ref.current.click()}
                className="btn-outline text-[10px] flex-1 justify-center">
          {value ? "Replace" : "Upload"}
        </button>
        {value && (
          <button type="button"
                  data-testid={`va-clear-${label}`}
                  onClick={onClear}
                  className="btn-outline text-[10px] px-2" title="Remove">
            <X className="w-3 h-3" strokeWidth={1.5}/>
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>{label}</label>
      <input className="luxe-input" data-testid={testid} {...rest}/>
    </div>
  );
}
